'use client';

import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

/**
 * Cooldown period after each fetch before another can be triggered
 * Prevents rapid successive fetches when loadMoreRef stays in view
 */
const FETCH_COOLDOWN_MS = 500;

function useInfiniteScroll<TData, TError>(
  infiniteQueryResult: UseInfiniteQueryResult<TData, TError>,
) {
  // Track if we're in cooldown period after a fetch (using ref to avoid effect setState issues)
  const isInCooldownRef = useRef(false);
  // Track cooldown timeout for cleanup
  const cooldownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track previous inView state to detect edge transitions (entering view)
  const wasInViewRef = useRef(false);

  // Configure the intersection observer with conservative settings for dropdowns
  const { ref, inView } = useInView({
    threshold: 0.8, // Trigger when element is 80% visible (user must scroll close to bottom)
    rootMargin: '0px', // No pre-loading margin to prevent aggressive fetching
  });

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
  } = infiniteQueryResult;

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimeoutRef.current !== null) {
        clearTimeout(cooldownTimeoutRef.current);
      }
    };
  }, []);

  // Handle fetch trigger with edge detection (only on entering view, not staying in view)
  useEffect(() => {
    const isEnteringView = inView === true && wasInViewRef.current === false;
    wasInViewRef.current = inView;

    // Only trigger on entering view, not while staying in view
    if (
      isEnteringView === true
      && hasNextPage === true
      && isFetchingNextPage === false
      && isFetching === false
      && isInCooldownRef.current === false
    ) {
      // Start cooldown period using ref (no setState in effect)
      isInCooldownRef.current = true;
      cooldownTimeoutRef.current = setTimeout(() => {
        isInCooldownRef.current = false;
      }, FETCH_COOLDOWN_MS);

      // Fetch the next page
      fetchNextPage({ cancelRefetch: false });
    }
  }, [inView, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching]);

  // Callback to manually trigger load more (for explicit user action)
  const loadMore = useCallback(() => {
    if (
      hasNextPage === true
      && isFetchingNextPage === false
      && isFetching === false
      && isInCooldownRef.current === false
    ) {
      isInCooldownRef.current = true;
      cooldownTimeoutRef.current = setTimeout(() => {
        isInCooldownRef.current = false;
      }, FETCH_COOLDOWN_MS);
      fetchNextPage({ cancelRefetch: false });
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isFetching]);

  return {
    ...infiniteQueryResult,
    loadMoreRef: ref,
    loadMore,
  };
}

export default useInfiniteScroll;
